import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Footer from './components/Footer';
import SplitPanel from './components/SplitPanel';
import FeaturePanel from './components/FeaturePanel';
import OAuthLoginButtons from './components/OAuthLoginButtons';
import {
  DigitalOceanLogo,
  VastAiLogo,
  VoltageParkLogo,
  LambdaCloudLogo,
  RunPodLogo,
  HuggingFaceLogo,
  VllmLogo,
  PyTorchLogo
} from './components/BrandLogos';
import { useAuth } from './utils/useAuth';
import { ENABLE_EMAIL_AUTH } from './utils/authConfig';
import { getApiUrl, getAuthHeaders } from './utils/apiConfig';
import { Input, InputGroup } from 'rsuite';
import CopyButton from './components/CopyButton';
import NavLinks from './components/NavLinks';
import { getSiteTheme, getSplitPanelTheme, getStoredDarkMode, persistDarkMode } from './styles/siteTheme';

const MAIN_CONFIG = {
  title: 'All Completed Tuning',
  tagline: 'Push your fine-tuning jobs to the cloud.'
};

const DOCS_URL = 'https://github.com/all-completed/tuning-proxy/blob/main/docs/API.md';
const CLIENT_DOCS_URL = 'https://github.com/all-completed/tuning-proxy/blob/main/docs/client.md';
const EXAMPLES_URL = 'https://github.com/all-completed/tuning-proxy/blob/main/docs/examples.md';
const INTEGRATIONS_URL = 'https://github.com/all-completed/tuning-proxy/blob/main/docs/integrations.md';

const ACCTL_RELEASES_URL = 'https://github.com/all-completed/acctl/releases/latest';
const ACCTL_INSTALL_CMD = 'curl -L https://github.com/all-completed/acctl/releases/latest/download/acctl-aarch64-apple-darwin -o acctl && chmod +x acctl';
const ACCTL_SUBMIT_SNIPPET = `# 1. Set your API key (or use --api-key)
export AC_API_KEY="your-api-token"

# 2. Submit a tuning job from any directory
./acctl tuning submit ./examples/hello-world \\
  --instance-type gpu-6000adax1-48gb \\
  --command "python train.py --output-dir \\$OUTPUT_MODEL"

# 3. Watch its progress
./acctl tuning status <JOB_ID> --watch`;

const API_SUBMIT_CURL = `curl -X POST https://tuning.all-completed.com/users/me/submit \\
  -H "Authorization: Bearer $AC_API_KEY" \\
  -F "archive=@my-job.tar.gz" \\
  -F "instance_type=gpu-6000adax1-48gb" \\
  -F "command=python train.py --output-dir \\$OUTPUT_MODEL"`;

const MCP_CONFIG_JSON = `{
  "mcpServers": {
    "tuning-proxy": {
      "url": "https://tuning.all-completed.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`;

const INFERENCE_CURL = `curl https://tuning.all-completed.com/v1/chat/completions \\
  -H "Authorization: Bearer $AC_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "my-fine-tuned-model",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`;

function GpuInstancesPreviewSvg({ title = 'GPU Instances Preview' }) {
  return (
    <svg
      viewBox="0 0 560 260"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      style={{ display: 'block', width: '100%', height: 'auto' }}
    >
      <defs>
        <linearGradient id="cardA" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1E293B" />
          <stop offset="1" stopColor="#0F172A" />
        </linearGradient>
        <linearGradient id="accentBlue" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#60A5FA" />
          <stop offset="1" stopColor="#2563EB" />
        </linearGradient>
        <linearGradient id="accentGreen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#34D399" />
          <stop offset="1" stopColor="#10B981" />
        </linearGradient>
        <linearGradient id="accentAmber" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FBBF24" />
          <stop offset="1" stopColor="#F59E0B" />
        </linearGradient>
        <filter id="cardShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#020617" floodOpacity="0.45" />
        </filter>
      </defs>

      <rect x="0" y="0" width="560" height="260" rx="22" fill="#0B1220" />

      {[0, 1, 2].map((i) => {
        const x = 24 + i * 176;
        const accents = ['url(#accentBlue)', 'url(#accentGreen)', 'url(#accentAmber)'];
        const labels = ['H100 · 80GB', 'L40S · 48GB', 'MI300X · 192GB'];
        const prices = ['$3.39/hr', '$1.57/hr', '$1.99/hr'];
        return (
          <g key={i} transform={`translate(${x}, 32)`} filter="url(#cardShadow)">
            <rect x="0" y="0" width="160" height="196" rx="16" fill="url(#cardA)" stroke="rgba(148,163,184,0.18)" />
            <rect x="14" y="16" width="34" height="10" rx="5" fill={accents[i]} />
            <text x="14" y="56" fontSize="14" fontWeight="800" fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" fill="#E2E8F0">
              {labels[i]}
            </text>
            <text x="14" y="78" fontSize="11" fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" fill="#94A3B8">
              On-demand GPU
            </text>

            <g transform="translate(14, 96)">
              {[0, 1, 2, 3].map((j) => (
                <rect key={j} x={0} y={j * 14} width={132 - j * 8} height="6" rx="3" fill="rgba(148,163,184,0.18)" />
              ))}
            </g>

            <text x="14" y="174" fontSize="13" fontWeight="800" fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" fill="#F8FAFC">
              {prices[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function detectPlatform() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { platform: 'macos', arch: 'arm64' };
  }

  const ua = (navigator.userAgent || '').toLowerCase();
  const platformStr = (navigator.platform || '').toLowerCase();

  let platform = 'macos';
  if (ua.includes('win')) platform = 'windows';
  else if (ua.includes('linux')) platform = 'linux';
  else if (ua.includes('mac') || platformStr.includes('mac')) platform = 'macos';

  let arch = null;
  if (ua.includes('arm64') || ua.includes('aarch64')) arch = 'arm64';
  else if (ua.includes('x86_64') || ua.includes('amd64') || ua.includes('win64')) arch = 'x86_64';

  if (!arch && platform === 'macos') {
    // Browsers on Apple Silicon spoof the UA as "Intel Mac OS X", so the UA alone is unreliable.
    // Probe WebGL renderer: Apple Silicon reports "Apple GPU"/"Apple M..." while Intel Macs expose Intel/AMD GPUs.
    arch = detectMacArchViaWebGL() || 'arm64';
  }

  if (!arch) arch = 'x86_64';

  return { platform, arch };
}

function detectMacArchViaWebGL() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return null;
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    const r = (renderer || '').toString().toLowerCase();
    if (!r) return null;
    if (r.includes('apple')) return 'arm64';
    if (r.includes('intel') || r.includes('amd') || r.includes('radeon')) return 'x86_64';
    return null;
  } catch (e) {
    return null;
  }
}

const GH_RELEASE = 'https://github.com/all-completed/acctl/releases/latest/download';
const ACCTL_DOWNLOADS = {
  macos: {
    title: 'macOS',
    items: [
      { key: 'arm64', label: 'macOS (Apple Silicon)', href: `${GH_RELEASE}/acctl-aarch64-apple-darwin` },
      { key: 'x86_64', label: 'macOS (Intel)', href: `${GH_RELEASE}/acctl-x86_64-apple-darwin` }
    ]
  },
  linux: {
    title: 'Linux',
    items: [
      { key: 'x86_64', label: 'Linux (x86_64)', href: `${GH_RELEASE}/acctl-x86_64-unknown-linux-musl` },
      { key: 'arm64', label: 'Linux (arm64)', href: `${GH_RELEASE}/acctl-aarch64-unknown-linux-gnu` }
    ]
  },
  windows: {
    title: 'Windows',
    items: [
      { key: 'x86_64', label: 'Windows (x86_64)', href: `${GH_RELEASE}/acctl-x86_64-pc-windows-msvc.exe` },
      { key: 'arm64', label: 'Windows (arm64)', href: `${GH_RELEASE}/acctl-aarch64-pc-windows-msvc.exe` }
    ]
  }
};

function Landing() {
  const auth = useAuth();
  const [formValue, setFormValue] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [tokenFetching, setTokenFetching] = useState(false);
  const [darkMode, setDarkMode] = useState(getStoredDarkMode);
  const detected = useMemo(detectPlatform, []);
  const [downloadTab, setDownloadTab] = useState(detected.platform);
  const [downloadArch, setDownloadArch] = useState(detected.arch);

  const isAuthed = auth.hasAuth0 && auth.isAuthenticated;

  const toggleTheme = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      persistDarkMode(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    const uad = navigator.userAgentData;
    if (!uad || typeof uad.getHighEntropyValues !== 'function') return;

    uad
      .getHighEntropyValues(['architecture', 'platform'])
      .then((values) => {
        const platform = (values.platform || '').toLowerCase();
        const arch = (values.architecture || '').toLowerCase();
        if (platform.includes('mac')) setDownloadTab('macos');
        else if (platform.includes('windows')) setDownloadTab('windows');
        else if (platform.includes('linux')) setDownloadTab('linux');

        if (arch.includes('arm')) setDownloadArch('arm64');
        else if (arch.includes('x86')) setDownloadArch('x86_64');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAuthed && auth.getAccessTokenSilently) {
      setTokenFetching(true);
      getAuthHeaders(auth.getAccessTokenSilently)
        .then((headers) => fetch(getApiUrl('/api/users/me/api-key'), { method: 'GET', headers }))
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to fetch')))
        .then((data) => {
          const meta = data?.metadata || {};
          setApiToken(meta.ac_api_key || meta.AC_API_KEY || '');
        })
        .catch(() => setApiToken(''))
        .finally(() => setTokenFetching(false));
    }
  }, [isAuthed, auth.getAccessTokenSilently]);

  const theme = useMemo(() => getSiteTheme(darkMode), [darkMode]);

  const goToDashboard = useCallback(() => {
    window.location.hash = '#/jobs';
  }, []);

  const handleEmailLogin = async () => {
    setErrors({});
    setErrorMessage('');
    if (!formValue.email || !formValue.email.includes('@')) {
      setErrors({ email: 'Valid email is required' });
      return;
    }
    if (!formValue.password) {
      setErrors({ password: 'Password is required' });
      return;
    }
    setLoading(true);
    try {
      if (auth.loginWithRedirect) {
        auth.loginWithRedirect({
          authorizationParams: {
            connection: 'Username-Password-Authentication',
            login_hint: formValue.email
          },
          screen_hint: 'login'
        });
      } else {
        throw new Error('Auth0 not configured');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Login failed');
      setLoading(false);
    }
  };

  const handleOAuthLogin = (connection) => {
    if (auth.loginWithRedirect) {
      auth.loginWithRedirect({ authorizationParams: { connection } });
    }
  };

  const styles = useMemo(() => {
    const container = {
      width: '100%',
      maxWidth: 1100,
      margin: '0 auto',
      padding: '0 16px'
    };

    const buttonBase = {
      appearance: 'none',
      border: `1px solid ${theme.border2}`,
      borderRadius: 12,
      padding: '10px 14px',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1,
      color: theme.text,
      background: 'transparent',
      cursor: 'pointer',
      transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease'
    };

    const inputBase = {
      width: '100%',
      background: theme.inputBg,
      border: `1px solid ${theme.border2}`,
      color: theme.text,
      borderRadius: 12,
      padding: '12px 14px',
      outline: 'none',
      fontSize: 14
    };

    const card = {
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      borderRadius: 20,
      boxShadow: theme.shadow
    };

    return {
      page: {
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowX: 'hidden'
      },
      glow: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: theme.glowBg
      },
      nav: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: `1px solid ${theme.border}`,
        background: theme.navBg,
        backdropFilter: 'blur(14px)'
      },
      navInner: {
        ...container,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 12,
        paddingTop: 14,
        paddingBottom: 14,
        minHeight: 52
      },
      brand: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        cursor: 'pointer',
        userSelect: 'none'
      },
      brandTitle: {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.02em'
      },
      brandBadge: {
        fontSize: 12,
        color: theme.muted,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        background: theme.brandBadgeBg
      },
      navLinks: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap'
      },
      navLink: {
        color: theme.muted,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 600
      },
      navCtas: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'flex-end'
      },
      btn: buttonBase,
      btnGhost: {
        ...buttonBase,
        background: theme.btnGhostBg
      },
      btnPrimary: {
        ...buttonBase,
        borderColor: darkMode ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.4)',
        background: darkMode
          ? 'linear-gradient(180deg, rgba(96, 165, 250, 0.24), rgba(59, 130, 246, 0.16))'
          : 'linear-gradient(180deg, #3B82F6, #2563EB)',
        color: theme.btnPrimaryColor
      },
      btnDisabled: {
        ...buttonBase,
        opacity: 0.5,
        cursor: 'not-allowed'
      },
      main: {
        flex: 1,
        width: '100%',
        padding: '64px 0 52px'
      },
      section: {
        ...container
      },
      hero: {
        textAlign: 'center',
        paddingTop: 8,
        paddingBottom: 26
      },
      kicker: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        background: theme.kickerBg,
        color: theme.muted,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      },
      heroTitle: {
        margin: '18px 0 10px',
        fontSize: 'clamp(44px, 7vw, 76px)',
        fontWeight: 900,
        letterSpacing: '-0.04em',
        lineHeight: 1.02
      },
      heroSubtitle: {
        margin: '0 auto',
        maxWidth: 780,
        color: theme.muted,
        fontSize: 16,
        lineHeight: 1.6
      },
      heroActions: {
        marginTop: 22,
        display: 'flex',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap'
      },
      featuresBoard: {
        marginTop: 34,
        display: 'grid',
        gap: 16
      },
      featureEyebrow: {
        margin: 0,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.muted2
      },
      featureTitle: {
        margin: '8px 0 0',
        fontSize: 20,
        fontWeight: 900,
        letterSpacing: '-0.02em'
      },
      featureText: {
        margin: '10px 0 0',
        color: theme.muted,
        fontSize: 14,
        lineHeight: 1.65
      },
      featureActions: {
        marginTop: 14,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10
      },
      previewCard: {
        ...card,
        flex: '1 1 360px',
        minWidth: 280,
        overflow: 'hidden'
      },
      previewBody: {
        padding: 18
      },
      logosWrap: {
        display: 'flex',
        gap: 28,
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        height: '100%'
      },
      logoItem: {
        display: 'grid',
        justifyItems: 'center',
        gap: 10
      },
      logoLabel: {
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: '-0.01em',
        color: theme.text
      },
      logoLink: {
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer'
      },
      loginWrap: {
        marginTop: 38,
        paddingTop: 18
      },
      loginPanelTitle: {
        margin: 0,
        fontSize: 22,
        fontWeight: 950,
        letterSpacing: '-0.03em'
      },
      loginPanelText: {
        margin: '12px 0 0',
        color: theme.muted,
        fontSize: 14,
        lineHeight: 1.7
      },
      alert: {
        marginTop: 14,
        border: `1px solid ${theme.alertBorder}`,
        background: theme.alertBg,
        color: theme.alertText,
        borderRadius: 14,
        padding: '10px 12px',
        fontSize: 13,
        lineHeight: 1.5
      },
      form: {
        marginTop: 16,
        display: 'grid',
        gap: 12
      },
      label: {
        display: 'grid',
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        color: theme.text
      },
      input: inputBase,
      inputError: {
        ...inputBase,
        borderColor: 'rgba(248, 113, 113, 0.55)'
      },
      fieldError: {
        marginTop: 6,
        color: theme.fieldErrorColor,
        fontSize: 12,
        lineHeight: 1.35
      },
      divider: {
        margin: '14px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: theme.muted2,
        fontSize: 12,
        fontWeight: 700
      },
      dividerLine: {
        flex: 1,
        height: 1,
        background: theme.border
      },
      oauthGrid: {
        display: 'grid',
        gap: 10
      },
      oauthBtn: {
        ...buttonBase,
        width: '100%',
        padding: '12px 14px',
        background: theme.oauthBtnBg,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      },
      codeBlock: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        background: theme.codeBlockBg,
        border: `1px solid ${theme.border2}`,
        borderRadius: 8,
        padding: '12px 14px'
      },
      codeBlockPre: {
        position: 'relative',
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        background: theme.codeBlockBg,
        border: `1px solid ${theme.border2}`,
        borderRadius: 8,
        padding: '12px 14px'
      },
      oauthBtnContent: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10
      },
      oauthIconWrap: {
        width: 18,
        height: 18,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.text
      },
      subtleLinkBtn: {
        ...buttonBase,
        width: '100%',
        marginTop: 12,
        background: 'transparent',
        borderColor: theme.border,
        color: theme.muted
      },
      platformTabs: {
        display: 'flex',
        gap: 6,
        padding: 4,
        borderRadius: 12,
        border: `1px solid ${theme.border}`,
        background: theme.codeBlockBg,
        marginBottom: 12,
        width: 'fit-content'
      },
      platformTab: (active) => ({
        appearance: 'none',
        border: 'none',
        borderRadius: 8,
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        background: active ? (darkMode ? 'rgba(96, 165, 250, 0.18)' : 'rgba(59, 130, 246, 0.15)') : 'transparent',
        color: active ? theme.text : theme.muted,
        transition: 'background 120ms ease, color 120ms ease'
      }),
      downloadList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      },
      downloadRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap'
      },
      recommendedTag: {
        fontSize: 11,
        fontWeight: 800,
        padding: '4px 8px',
        borderRadius: 999,
        background: darkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.18)',
        color: darkMode ? '#86EFAC' : '#15803D',
        textTransform: 'uppercase',
        letterSpacing: '0.08em'
      },
      footer: {
        borderTop: `1px solid ${theme.border}`,
        background: theme.footerBg,
        color: theme.muted,
        textAlign: 'center',
        padding: '16px 20px',
        fontSize: 14,
        '--rs-primary-500': theme.primary,
        '--rs-text-secondary': theme.muted,
        '--rs-border-secondary': theme.border
      }
    };
  }, [theme, darkMode]);

  const downloadItems = ACCTL_DOWNLOADS[downloadTab].items;

  return (
    <div style={styles.page}>
      <div style={styles.glow} />

      <nav style={styles.nav} aria-label="Primary">
        <div style={styles.navInner}>
          <div
            style={styles.brand}
            role="button"
            tabIndex={0}
            onClick={() => { window.location.hash = '#/'; }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.location.hash = '#/'; }}
          >
            <div style={styles.brandTitle}>{MAIN_CONFIG.title}</div>
            <button
              type="button"
              style={{
                ...styles.brandBadge,
                cursor: 'pointer',
                margin: 0,
                font: 'inherit'
              }}
              onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? 'Dark' : 'Light'}
            </button>
          </div>

          <div style={styles.navLinks}>
            <NavLinks navLinkStyle={styles.navLink} theme={theme} />
          </div>

          <div style={styles.navCtas}>
            {isAuthed ? (
              <button
                type="button"
                style={styles.btnPrimary}
                onClick={goToDashboard}
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  type="button"
                  style={styles.btnGhost}
                  onClick={() => { window.location.hash = '#/login'; }}
                >
                  Log in
                </button>
                {ENABLE_EMAIL_AUTH && (
                  <button type="button" style={styles.btnPrimary} onClick={() => { window.location.hash = '#/signup'; }}>
                    Sign up
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      <main style={styles.main}>
        <section style={styles.section}>
          <header style={styles.hero}>
            <h1 style={styles.heroTitle}>{MAIN_CONFIG.title}</h1>
            <p style={styles.heroSubtitle}>
              {MAIN_CONFIG.tagline} Submit fine-tuning workloads from your terminal, run them on on-demand GPU droplets,
              and serve the resulting models — all behind a single OpenAI-compatible API.
            </p>
            <div style={styles.heroActions}>
              {isAuthed ? (
                <button type="button" style={styles.btnPrimary} onClick={goToDashboard}>
                  Open Dashboard
                </button>
              ) : (
                <button
                  type="button"
                  style={styles.btnPrimary}
                  onClick={() => { window.location.hash = '#/login'; }}
                >
                  Get Started
                </button>
              )}
              <a
                style={{ ...styles.btnGhost, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
              >
                Read Docs
              </a>
            </div>

            <div style={styles.featuresBoard} aria-label="Highlights">
              <SplitPanel theme={getSplitPanelTheme(theme)} left={
                  <FeaturePanel
                    styles={styles}
                    subTitle="CLI"
                    title="acctl — submit jobs from your terminal"
                    description="A small native CLI for packaging your project, picking a GPU instance type and pushing the job to the cloud. Works with API keys or your Auth0 session."
                    footerStyle={{ alignItems: 'flex-start' }}
                    footer={
                      <>
                        <div style={styles.platformTabs} role="tablist" aria-label="Platform">
                          {Object.keys(ACCTL_DOWNLOADS).map((key) => (
                            <button
                              key={key}
                              type="button"
                              role="tab"
                              aria-selected={downloadTab === key}
                              style={styles.platformTab(downloadTab === key)}
                              onClick={() => setDownloadTab(key)}
                            >
                              {ACCTL_DOWNLOADS[key].title}
                            </button>
                          ))}
                        </div>
                        <div style={styles.downloadList}>
                          {downloadItems.map((item) => {
                            const isRecommended = downloadTab === detected.platform && item.key === downloadArch && !!item.href;
                            if (!item.href) {
                              return (
                                <div key={item.key} style={styles.downloadRow}>
                                  <button type="button" style={styles.btnDisabled} disabled>
                                    {item.label}
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div key={item.key} style={styles.downloadRow}>
                                <a
                                  href={item.href}
                                  style={{ ...(isRecommended ? styles.btnPrimary : styles.btn), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                                >
                                  {item.label}
                                </a>
                                {isRecommended && <span style={styles.recommendedTag}>Recommended</span>}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                          <a
                            href={CLIENT_DOCS_URL}
                            target="_blank"
                            rel="noreferrer"
                            style={{ ...styles.btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                          >
                            CLI Docs
                          </a>
                          <a
                            href={ACCTL_RELEASES_URL}
                            target="_blank"
                            rel="noreferrer"
                            style={{ ...styles.btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                          >
                            All Releases ↗
                          </a>
                        </div>
                      </>
                    }
                  />
                }
                right={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                    <div style={styles.codeBlock}>
                      <code style={{ flex: 1, fontSize: 13, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ACCTL_INSTALL_CMD}</code>
                      <CopyButton text={ACCTL_INSTALL_CMD} successMessage="Copied to clipboard" errorMessage="Failed to copy" />
                    </div>
                    <div style={styles.divider} aria-hidden="true">
                      <div style={styles.dividerLine} />
                      <div>submit_job.sh</div>
                      <div style={styles.dividerLine} />
                    </div>
                    <div style={{ ...styles.codeBlockPre, position: 'relative' }}>
                      <pre style={{ flex: 1, margin: 0, minWidth: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', overflow: 'auto', textAlign: 'left' }}>{ACCTL_SUBMIT_SNIPPET}</pre>
                      <CopyButton text={ACCTL_SUBMIT_SNIPPET} successMessage="Copied to clipboard" errorMessage="Failed to copy" />
                    </div>
                  </div>
                }
              />

              <SplitPanel theme={getSplitPanelTheme(theme)} left={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                    <div style={{ ...styles.codeBlockPre, position: 'relative' }}>
                      <pre style={{ flex: 1, margin: 0, minWidth: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', overflow: 'auto', textAlign: 'left' }}>{API_SUBMIT_CURL}</pre>
                      <CopyButton text={API_SUBMIT_CURL} successMessage="Copied to clipboard" errorMessage="Failed to copy" />
                    </div>
                    <div style={styles.divider} aria-hidden="true">
                      <div style={styles.dividerLine} />
                      <div>~/.cursor/mcp.json</div>
                      <div style={styles.dividerLine} />
                    </div>
                    <div style={{ ...styles.codeBlockPre, position: 'relative' }}>
                      <pre style={{ flex: 1, margin: 0, minWidth: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflow: 'auto', textAlign: 'left', opacity: isAuthed && apiToken ? 1 : 0.4 }}>
                        {MCP_CONFIG_JSON.replace('YOUR_API_KEY', isAuthed && apiToken ? apiToken : 'YOUR_API_KEY')}
                      </pre>
                      {isAuthed && apiToken ? (
                        <CopyButton
                          text={MCP_CONFIG_JSON.replace('YOUR_API_KEY', apiToken)}
                          successMessage="Copied to clipboard"
                          errorMessage="Failed to copy"
                        />
                      ) : (
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <button
                            type="button"
                            style={{ ...styles.btn, pointerEvents: 'auto', background: theme.panel }}
                            onClick={() => {
                              try { sessionStorage.setItem('postLoginRedirect', '/'); } catch (_) { /* ignore */ }
                              window.location.hash = '#/login';
                            }}
                          >
                            Sign in to get API token
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                }
                right={
                  <FeaturePanel
                    styles={styles}
                    subTitle="API & MCP"
                    title="REST API and Model Context Protocol"
                    description="Submit and manage jobs over a simple REST API. Every endpoint is also exposed as an MCP tool so AI agents (Cursor, Claude Code, OpenClaw) can drive the platform directly."
                    footerStyle={{ alignItems: 'flex-start' }}
                    footer={
                      <>
                        <a
                          href={DOCS_URL}
                          target="_blank"
                          rel="noreferrer"
                          style={{ ...styles.btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                        >
                          API Reference
                        </a>
                        <a
                          href={EXAMPLES_URL}
                          target="_blank"
                          rel="noreferrer"
                          style={{ ...styles.btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                        >
                          Examples
                        </a>
                      </>
                    }
                  />
                }
              />

              <SplitPanel theme={getSplitPanelTheme(theme)} left={
                  <FeaturePanel
                    styles={styles}
                    subTitle="GPU Instances"
                    title="On-demand GPUs across providers"
                    description="Pick from RTX 4000 Ada to H200 and MI300X. Droplets are created per job, drained on completion, and routed across DigitalOcean, Vast.ai, Voltage Park, Lambda, and RunPod to find capacity fast."
                    footer={
                      <a
                        href={INTEGRATIONS_URL}
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                      >
                        Provider Setup
                      </a>
                    }
                  />
                }
                right={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
                    <div style={{ borderRadius: 16, overflow: 'hidden', border: `1px solid ${theme.border}` }}>
                      <GpuInstancesPreviewSvg />
                    </div>
                    <div style={{ ...styles.logosWrap, height: 'auto', justifyContent: 'space-around' }} aria-label="GPU providers">
                      <a
                        href="https://www.digitalocean.com/products/gpu-droplets"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="DigitalOcean"
                        title="DigitalOcean"
                      >
                        <DigitalOceanLogo size={56} />
                        <div style={styles.logoLabel}>DigitalOcean</div>
                      </a>
                      <a
                        href="https://vast.ai/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="Vast.ai"
                        title="Vast.ai"
                      >
                        <VastAiLogo size={56} />
                        <div style={styles.logoLabel}>Vast.ai</div>
                      </a>
                      <a
                        href="https://www.voltagepark.com/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="Voltage Park"
                        title="Voltage Park"
                      >
                        <VoltageParkLogo size={56} />
                        <div style={styles.logoLabel}>Voltage Park</div>
                      </a>
                      <a
                        href="https://lambda.ai/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="Lambda"
                        title="Lambda"
                      >
                        <LambdaCloudLogo size={56} />
                        <div style={styles.logoLabel}>Lambda</div>
                      </a>
                      <a
                        href="https://www.runpod.io/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="RunPod"
                        title="RunPod"
                      >
                        <RunPodLogo size={56} />
                        <div style={styles.logoLabel}>RunPod</div>
                      </a>
                    </div>
                  </div>
                }
              />

              <SplitPanel theme={getSplitPanelTheme(theme)} left={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                    <div style={{ ...styles.codeBlockPre, position: 'relative' }}>
                      <pre style={{ flex: 1, margin: 0, minWidth: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', overflow: 'auto', textAlign: 'left' }}>{INFERENCE_CURL}</pre>
                      <CopyButton text={INFERENCE_CURL} successMessage="Copied to clipboard" errorMessage="Failed to copy" />
                    </div>
                    <div style={{ ...styles.logosWrap, height: 'auto', marginTop: 4 }} aria-label="Inference stack">
                      <a
                        href="https://docs.vllm.ai/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="vLLM"
                        title="vLLM"
                      >
                        <VllmLogo size={64} />
                        <div style={styles.logoLabel}>vLLM</div>
                      </a>
                      <a
                        href="https://huggingface.co/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="Hugging Face"
                        title="Hugging Face"
                      >
                        <HuggingFaceLogo size={64} />
                        <div style={styles.logoLabel}>Hugging Face</div>
                      </a>
                      <a
                        href="https://pytorch.org/"
                        target="_blank"
                        rel="noreferrer"
                        style={{ ...styles.logoItem, ...styles.logoLink }}
                        aria-label="PyTorch"
                        title="PyTorch"
                      >
                        <PyTorchLogo size={64} />
                        <div style={styles.logoLabel}>PyTorch</div>
                      </a>
                    </div>
                  </div>
                }
                right={
                  <FeaturePanel
                    styles={styles}
                    subTitle="Inference"
                    title="OpenAI-compatible inference for your models"
                    description="Trained models are stored in S3 and served behind an OpenAI-compatible /v1 API. Stand up vLLM-backed endpoints for your fine-tuned checkpoints in a few clicks."
                    footer={
                      <button
                        type="button"
                        style={styles.btn}
                        onClick={() => {
                          if (isAuthed) {
                            window.location.hash = '#/inference';
                          } else {
                            try { sessionStorage.setItem('postLoginRedirect', '/inference'); } catch (_) { /* ignore */ }
                            window.location.hash = '#/login';
                          }
                        }}
                      >
                        Open Inference
                      </button>
                    }
                  />
                }
              />
            </div>
          </header>

          {!isAuthed && (
            <div id="login" style={styles.loginWrap}>
              <SplitPanel theme={getSplitPanelTheme(theme)}
                left={
                  <>
                    <h2 style={styles.loginPanelTitle}>Welcome back</h2>
                    <p style={styles.loginPanelText}>Sign in to submit jobs, manage models, and add credit to your balance.</p>
                  </>
                }
                right={
                  <>
                    {errorMessage ? <div style={styles.alert}>{errorMessage}</div> : null}

                    {ENABLE_EMAIL_AUTH ? (
                      <form
                        style={styles.form}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleEmailLogin();
                        }}
                      >
                        <div>
                          <label style={styles.label} htmlFor="email">
                            Email
                            <input
                              id="email"
                              autoComplete="email"
                              type="email"
                              placeholder="you@example.com"
                              value={formValue.email}
                              onChange={(e) => setFormValue((v) => ({ ...v, email: e.target.value }))}
                              style={errors.email ? styles.inputError : styles.input}
                              disabled={loading}
                            />
                          </label>
                          {errors.email ? <div style={styles.fieldError}>{errors.email}</div> : null}
                        </div>

                        <div>
                          <label style={styles.label} htmlFor="password">
                            Password
                            <input
                              id="password"
                              autoComplete="current-password"
                              type="password"
                              placeholder="Password"
                              value={formValue.password}
                              onChange={(e) => setFormValue((v) => ({ ...v, password: e.target.value }))}
                              style={errors.password ? styles.inputError : styles.input}
                              disabled={loading}
                            />
                          </label>
                          {errors.password ? <div style={styles.fieldError}>{errors.password}</div> : null}
                        </div>

                        <button type="submit" style={{ ...styles.btnPrimary, width: '100%', padding: '12px 14px' }} disabled={loading}>
                          {loading ? 'Signing in…' : 'Log in'}
                        </button>
                      </form>
                    ) : null}

                    <OAuthLoginButtons
                      onOAuthLogin={handleOAuthLogin}
                      loading={loading}
                      styles={styles}
                      mutedColor={theme.muted}
                    />

                    {ENABLE_EMAIL_AUTH ? (
                      <button type="button" style={styles.subtleLinkBtn} onClick={() => { window.location.hash = '#/signup'; }} disabled={loading}>
                        Don&apos;t have an account? Sign up
                      </button>
                    ) : null}
                  </>
                }
              />
            </div>
          )}

          {isAuthed && (
            <div id="api-token" style={styles.loginWrap}>
              <SplitPanel theme={getSplitPanelTheme(theme)}
                left={
                  <>
                    <h2 style={styles.loginPanelTitle}>Your API token</h2>
                    <p style={styles.loginPanelText}>
                      Use this token with <code style={{ fontFamily: 'monospace' }}>acctl</code>, the REST API, or any MCP-compatible client.
                      Set it as the <code style={{ fontFamily: 'monospace' }}>AC_API_KEY</code> environment variable.
                    </p>
                  </>
                }
                right={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <InputGroup style={{ background: theme.codeBlockBg, border: `1px solid ${theme.border2}`, borderRadius: 8, color: theme.text }}>
                      <Input
                        value={tokenFetching ? 'Loading…' : (apiToken || 'No API token yet — open Settings → Manage API Token')}
                        readOnly
                        style={{ background: 'transparent', color: theme.text, WebkitTextFillColor: theme.text, opacity: 1 }}
                      />
                      <InputGroup.Button>
                        <CopyButton
                          text={apiToken}
                          disabled={tokenFetching || !apiToken}
                          successMessage="Token copied to clipboard"
                          errorMessage="Failed to copy token"
                        />
                      </InputGroup.Button>
                    </InputGroup>
                    <button type="button" style={styles.btnPrimary} onClick={goToDashboard}>
                      Open Dashboard
                    </button>
                  </div>
                }
              />
            </div>
          )}
        </section>
      </main>

      <Footer compact style={styles.footer} />
    </div>
  );
}

export default Landing;
